import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { MessageCircle, ShoppingBag, Network, Store, Tag, Shield, LogOut, Bell, Sparkles, ChevronDown, Award, Briefcase } from 'lucide-react';
import { IndivalifeLogo } from '@/components/IndivalifeLogo';
import { InstallPWA } from '@/components/InstallPWA';
import { InstallTopBar } from '@/components/InstallTopBar';
import { AnnouncementsBanner } from '@/components/AnnouncementsBanner';
import { EnablePush } from '@/components/EnablePush';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Toaster } from '@/components/ui/sonner';

const navItems = [
  { to: '/store', label: 'Store', icon: ShoppingBag, testid: 'nav-store' },
  { to: '/business', label: 'Business', icon: Briefcase, testid: 'nav-business' },
  { to: '/my-store', label: 'My Store', icon: Store, testid: 'nav-mystore' },
  { to: '/network', label: 'Network', icon: Network, testid: 'nav-network' },
];

const RankBadgeNav = ({ badge }) => (
  <NavLink to="/rank" data-testid="nav-rank" title="Your rank & team"
    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-[var(--bz-orange-tint)]' : 'hover:bg-[var(--bz-soft)]'}`}>
    <div className="h-10 w-10 rounded-full overflow-hidden ring-2 ring-[var(--bz-orange)] shadow grid place-items-center bg-white shrink-0">
      {badge.badge_url
        ? <img src={badge.badge_url} alt="Rank" className="h-full w-full object-cover" />
        : <Award className="h-6 w-6 text-[var(--bz-orange)]" />}
    </div>
    <span className="text-sm font-bold text-[var(--bz-ink)]">{badge.rank || 'Your Rank'}</span>
  </NavLink>
);

const MobileRankBadge = ({ badge }) => (
  <NavLink to="/rank" data-testid="mobile-nav-rank" className="relative flex flex-col items-center justify-end pb-1">
    {({ isActive }) => (
      <>
        <div className={`-mt-7 h-16 w-16 rounded-full grid place-items-center bg-white shadow-xl ring-[3px] transition-transform active:scale-95 ${isActive ? 'ring-[var(--bz-orange)]' : 'ring-white'} overflow-hidden`}>
          {badge.badge_url
            ? <img src={badge.badge_url} alt="Rank" className="h-full w-full object-cover" />
            : <Award className="h-8 w-8 text-[var(--bz-orange)]" />}
        </div>
        <span className={`text-[9px] font-bold mt-0.5 ${isActive ? 'text-[var(--bz-orange)]' : 'text-[var(--bz-muted)]'}`}>{badge.rank || 'Rank'}</span>
      </>
    )}
  </NavLink>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);
  const [rankBadge, setRankBadge] = useState(null);

  useEffect(() => {
    let alive = true;
    if (user?.is_member) {
      api.get('/network/rank-badge').then(r => { if (alive) setRankBadge(r.data); }).catch(() => {});
    }
    return () => { alive = false; };
  }, [user]);

  const showBadge = rankBadge?.show;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Toaster position="top-right" richColors />
      <InstallTopBar />

      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-[var(--bz-line)] bg-white">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/deals')} data-testid="logo-home" aria-label="Home">
              <IndivalifeLogo size={36} withWordmark />
            </button>
            <span className="hidden md:inline-block text-xs px-2 py-0.5 rounded-full bg-[var(--bz-green-tint)] text-[#1E3A8A] font-semibold tracking-wider uppercase">v1.0</span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3 flex-nowrap">
            <InstallPWA />
            <div className="hidden sm:flex items-center gap-2">
              <EnablePush />
            </div>

            <button className="bz-btn-ghost !p-2 shrink-0" data-testid="notifications-btn" onClick={() => navigate('/notifications')} aria-label="Notifications">
              <Bell className="h-4 w-4" />
            </button>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-[var(--bz-line)] pl-1 pr-2 sm:pr-3 py-1 hover:bg-[var(--bz-soft)] shrink-0">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback>{(user.nickname || user.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-semibold">{user.nickname || user.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs">{user.email}{user.personal_id ? ` · ${user.personal_id}` : ''}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">Profile & Settings</DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Shield className="h-4 w-4 mr-2" /> Admin Control Room
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Sidebar */}
        <aside className="hidden md:flex flex-col w-56 border-r border-[var(--bz-line)] py-4 px-3 sticky top-[64px] self-start h-[calc(100vh-64px)]">
          <nav className="flex flex-col gap-1">
            {navItems.map(it => (
              <React.Fragment key={it.to}>
                <NavLink to={it.to} data-testid={it.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${isActive ? 'bg-[var(--bz-green-tint)] text-[#1E3A8A]' : 'text-[var(--bz-ink)] hover:bg-[var(--bz-soft)]'}`
                  }>
                  <it.icon className="h-4 w-4" /> {it.label}
                </NavLink>
                {showBadge && it.to === '/business' && <RankBadgeNav badge={rankBadge} />}
              </React.Fragment>
            ))}
          </nav>
          <div className="mt-auto pt-6 text-xs text-[var(--bz-muted)] tracking-wider uppercase font-semibold">
            INDIVALIFE © 2026
          </div>
        </aside>

        {/* Mobile bottom nav */}
        <nav className={`fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t border-[var(--bz-line)] grid ${showBadge ? 'grid-cols-5' : 'grid-cols-4'}`}>
          {navItems.map(it => (
            <React.Fragment key={it.to}>
              <NavLink to={it.to} data-testid={`mobile-${it.testid}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2 text-[10px] text-center font-semibold transition-colors border-t-2 ${isActive ? 'text-[var(--bz-orange)] border-[var(--bz-orange)] bg-[var(--bz-orange-tint)]' : 'text-[var(--bz-muted)] border-transparent'}`}>
                <it.icon className="h-5 w-5 mb-0.5" /> {it.label}
              </NavLink>
              {showBadge && it.to === '/business' && <MobileRankBadge badge={rankBadge} />}
            </React.Fragment>
          ))}
        </nav>

        <main className="flex-1 min-w-0 pb-20 md:pb-6 bz-scroll">
          <div className="px-4 pt-4 md:px-6">
            <AnnouncementsBanner />
          </div>
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default Layout;
